import { useLiveQuery } from 'dexie-react-hooks';
import { AlertTriangle, CheckCircle2, Cloud, CloudOff, RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { onlineManager } from '@tanstack/react-query';
import { SyncEngine } from '../features/sync/SyncEngine';
import { DialogHeader } from './ui/DialogHeader';
import { db } from '../db/schema';
import type { MutationRow } from '../db/schema';
import { cn } from '../lib/cn';

function useOnline(): boolean {
  const [online, setOnline] = useState(onlineManager.isOnline());
  useEffect(() => onlineManager.subscribe(setOnline), []);
  return online;
}

// Small status pill + drawer that exposes pending/failed/dead mutations to the
// user. Critical on mobile: vendors need to know at a glance whether their
// leads made it to the server, without trusting the browser's online state
// (which lies often — a Wi-Fi connection that's alive but can't reach the API).
//
// `hideWhenSynced`: passe-le à `true` dans les contextes "distraction-free"
// (wizard de création de fiche) pour masquer le badge vert "À jour". Le
// badge n'apparaît alors que quand quelque chose mérite attention
// (offline, pending, errors).
function mutationLabel(m: MutationRow): string {
  if (m.entity === 'pin_switch') return 'Changement de vendeur';
  return `${m.op === 'create' ? 'Création' : 'Modification'} · fiche`;
}

export function SyncStatusBadge({ hideWhenSynced = false }: { hideWhenSynced?: boolean } = {}) {
  const [open, setOpen] = useState(false);
  const online = useOnline();
  const mutations = useLiveQuery(() => db.mutations.toArray(), [], []);

  const pending = mutations.filter((m) => m.status === 'queued' || m.status === 'in_flight').length;
  const failed = mutations.filter((m) => m.status === 'failed' || m.status === 'dead').length;
  const isHealthy = online && pending === 0 && failed === 0;

  if (hideWhenSynced && isHealthy) return null;

  const icon = !online ? (
    <CloudOff className="h-4 w-4" />
  ) : failed > 0 ? (
    <AlertTriangle className="h-4 w-4" />
  ) : pending > 0 ? (
    <Cloud className="h-4 w-4 animate-pulse" />
  ) : (
    <CheckCircle2 className="h-4 w-4" />
  );

  const label = !online
    ? 'Hors ligne'
    : failed > 0
      ? `${failed} erreur${failed > 1 ? 's' : ''}`
      : pending > 0
        ? `${pending} en cours`
        : 'À jour';

  const color = !online
    ? 'text-amber-700 bg-amber-50 border-amber-200'
    : failed > 0
      ? 'text-red-700 bg-red-50 border-red-200'
      : pending > 0
        ? 'text-blue-700 bg-blue-50 border-blue-200'
        : 'text-emerald-700 bg-emerald-50 border-emerald-200';

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-meta font-medium transition-colors',
          color,
        )}
        aria-label="État de synchronisation"
      >
        {icon}
        <span className="hidden sm:inline">{label}</span>
      </button>
      {open && <SyncDrawerContent onClose={() => setOpen(false)} />}
    </>
  );
}

function SyncDrawerContent({ onClose }: { onClose: () => void }) {
  const online = useOnline();
  const mutations = useLiveQuery(() => db.mutations.orderBy('nextAttemptAt').toArray(), [], []);

  const queued = mutations.filter((m) => m.status === 'queued' || m.status === 'in_flight');
  const errored = mutations.filter((m) => m.status === 'failed' || m.status === 'dead');

  // Portal → document.body : le badge est souvent rendu à l'intérieur d'un
  // ancêtre `backdrop-blur` (header wizard sticky, header AppShell mobile),
  // ce qui crée un stacking context et piège tout `position: fixed` enfant.
  // Conséquence visible : la panel blanche du drawer était transparente, on
  // voyait les cartes verticales en transparence (cf. screenshot 2026-05-11).
  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-ink/40"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute right-0 top-0 h-dvh w-full max-w-md bg-white shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader title="Synchronisation" icon={RefreshCw} onClose={onClose} />

        <div className="p-4 border-b flex items-center justify-between gap-3">
          <div className="text-body">
            <div className="font-medium">{online ? 'En ligne' : 'Hors ligne'}</div>
            <div className="text-ink-mute text-meta">
              {queued.length} en attente · {errored.length} en erreur
            </div>
          </div>
          {online && (
            <button
              type="button"
              onClick={() => void SyncEngine.forceFlush()}
              className="inline-flex items-center gap-1 text-meta text-ink-soft hover:text-ink"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Tout relancer
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {mutations.length === 0 && (
            <div className="p-8 text-center text-body text-ink-mute">Tout est synchronisé.</div>
          )}

          {queued.length > 0 && (
            <section className="p-4 border-b">
              <h3 className="text-micro font-semibold uppercase tracking-[0.08em] text-ink-mute mb-2">
                En attente ({queued.length})
              </h3>
              <ul className="space-y-2">
                {queued.map((m) => (
                  <li key={m.id} className="text-body">
                    <div className="font-medium">{mutationLabel(m)}</div>
                    <div className="text-meta text-ink-mute">
                      {m.attempts > 0 && `tentatives: ${m.attempts} · `}
                      next: {new Date(m.nextAttemptAt).toLocaleTimeString('fr-FR')}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {errored.length > 0 && (
            <section className="p-4">
              <h3 className="text-micro font-semibold uppercase tracking-[0.08em] text-red-700 mb-2">
                En erreur ({errored.length})
              </h3>
              <ul className="space-y-2">
                {errored.map((m) => (
                  <li
                    key={m.id}
                    className="text-body rounded-lg bg-red-50 border border-red-100 p-2"
                  >
                    <div className="font-medium text-red-800">{mutationLabel(m)}</div>
                    {m.lastError && (
                      <div className="text-meta text-red-700 mt-1 break-words">{m.lastError}</div>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>

        <footer className="p-3 border-t text-meta text-ink-mute text-center">
          {online
            ? `TanStack online: ${onlineManager.isOnline()}`
            : 'Les mutations seront envoyées au retour du réseau'}
        </footer>
      </div>
    </div>,
    document.body,
  );
}
